import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, TextField, Button, Box, Grid, CircularProgress, Card, CardContent, Stack
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import provinciaService from '../../services/provinciaService';

interface ProvinciaFormData {
  codigoProvincia: number;
  nombreProvincia: string;
  cmdgr?: number;
}

const defaultForm: ProvinciaFormData = {
  codigoProvincia: 0,
  nombreProvincia: '',
  cmdgr: 0
};

const ProvinciaForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { codigoProvincia } = useParams();
  const [formData, setFormData] = useState<ProvinciaFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadProvincia = useCallback(async () => {
    if (!codigoProvincia || codigoProvincia === 'nuevo') return;
    try {
      setLoading(true);
      const provincia = await provinciaService.getProvincia(Number(codigoProvincia));
      setFormData({
        codigoProvincia: provincia.codigoProvincia,
        nombreProvincia: provincia.nombreProvincia || '',
        cmdgr: provincia.cmdgr
      });
    } catch (error) {
      enqueueSnackbar('Error al cargar la provincia', { variant: 'error' });
      navigate('/sistema/provincias');
    } finally {
      setLoading(false);
    }
  }, [codigoProvincia, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (codigoProvincia && codigoProvincia !== 'nuevo') loadProvincia();
  }, [codigoProvincia, loadProvincia]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'codigoProvincia' || name === 'cmdgr') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      if (!formData.nombreProvincia.trim()) {
        enqueueSnackbar('El nombre de la provincia es requerido', { variant: 'warning' });
        return;
      }

      if (codigoProvincia && codigoProvincia !== 'nuevo') {
        await provinciaService.updateProvincia(Number(codigoProvincia), formData);
      } else {
        await provinciaService.createProvincia(formData);
      }

      enqueueSnackbar('Provincia guardada exitosamente', { variant: 'success' });
      navigate('/sistema/provincias');
    } catch (error: any) {
      console.error('Error al guardar provincia:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar la provincia', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sistema/provincias')}
              sx={{ textTransform: 'none' }}
            >
              Provincias
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {codigoProvincia && codigoProvincia !== 'nuevo' ? 'Editar Provincia' : 'Nueva Provincia'}
            </Typography>
          </Stack>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                  <TextField 
                    name="codigoProvincia" 
                    label="Código" 
                    type="number"
                    value={formData.codigoProvincia} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!(codigoProvincia && codigoProvincia !== 'nuevo')}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  name="nombreProvincia" 
                  label="Nombre de Provincia" 
                  value={formData.nombreProvincia} 
                  onChange={handleChange} 
                  fullWidth 
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField 
                  name="cmdgr" 
                  label="CMDGR" 
                  type="number"
                  value={formData.cmdgr || ''} 
                  onChange={handleChange} 
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/sistema/provincias')} 
                disabled={saving}
                sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProvinciaForm;
